import { z } from 'zod'

import { moneySchema, percentSchema } from '@/schemas/common'

/**
 * MoneyMitra Weekly, in the app's own vocabulary.
 *
 * Wire shapes live in `@/api/schema` and are never restated here; this file
 * describes what the app holds AFTER `newsletterService`'s mappers have run,
 * so every name is camelCase and every decimal is a branded `Money`.
 *
 * Four rules this file turns on, all four verified against mm-be `origin/dev`
 * (`services/newsletter/`, `api/v5/newsletters/serializers.py`):
 *
 *  1. DECIMALS ARE STRINGS, so they are `moneySchema` / `percentSchema` — the
 *     same convention `src/schemas/portfolio.ts` uses and for the same reason
 *     (`COERCE_DECIMAL_TO_STRING: True`). See the note on `newsletterMetric`
 *     below for the ONE field that looks like a decimal and is not.
 *  2. NOT RECORDED IS NOT ZERO. `series[].value`, a sector row's
 *     `open`/`close`/`change` and `readTimeMinutes` are nullable on purpose:
 *     mm-be's `sections.py` coerces a missing/blank/garbage number to `None`
 *     rather than a filler 0. A chart must draw a gap and a table cell a dash;
 *     a 0 here paints a false floor that reads as a real week.
 *  3. `sections` is an ORDERED, HETEROGENEOUS list — a real discriminated
 *     union keyed by `type`, with exactly four members. It may be empty, for
 *     an issue that was authored but never filled in.
 *  4. Nothing here is pre-formatted by us. `publishedAt` stays the wire's ISO
 *     string and the screen decides how to render it, exactly as
 *     `isoDateSchema` in `portfolio.ts` keeps a `DateField` a string.
 */

// ── Primitives ───────────────────────────────────────────────────────────────

/**
 * A DRF `DateTimeField` on the wire: `"2026-09-13T04:15:00Z"`.
 *
 * A string, never a `Date`. The issue's publish moment is decided by the
 * server under Django's `TIME_ZONE` (Asia/Kathmandu), and round-tripping it
 * through a JS `Date` in the data layer would let a browser in another zone
 * decide which day "Issue 20" belongs to. The shape check is deliberately
 * loose — it proves the field is a timestamp, not that the calendar is sane.
 */
export const isoDateTimeSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T[\d:.]+(Z|[+-]\d{2}:\d{2})$/, 'Expected an ISO date-time')

// ── Section building blocks ──────────────────────────────────────────────────

/**
 * One tile in the "NEPSE this week" strip.
 *
 * `value` is a PLAIN STRING and deliberately NOT `moneySchema`, which is the
 * one place this domain departs from the portfolio convention. The strip is
 * heterogeneous and already formatted at publish time — `"2,622.48"`,
 * `"Rs 8.2 Arba"`, `"−0.73%"` — so it is display copy, not a decimal. Two
 * consequences, both load-bearing:
 *
 *   - `moneySchema`'s regex (`^-?\d+(\.\d+)?$`) would REJECT every one of
 *     those samples, so typing it as money would fail the parse outright.
 *   - It must be rendered verbatim. Re-parsing it would also have to survive
 *     the minus sign mm-be actually emits, U+2212 (`−`), not ASCII `-`.
 */
export const newsletterMetricSchema = z.object({
  label: z.string(),
  /** Already formatted upstream. Render as-is; never re-parse or re-format. */
  value: z.string(),
})

/**
 * One point on the weekly index chart.
 *
 * `null` is a gap in the series, not a zero — see rule 2. A chart built from
 * these must skip the point rather than plot it at the axis floor.
 */
export const newsletterSeriesPointSchema = z.object({
  label: z.string(),
  value: moneySchema.nullable(),
})

/** One bullet under "Market events": a headline and its one-line detail. */
export const newsletterMarketEventSchema = z.object({
  headline: z.string(),
  detail: z.string(),
})

/** The "We think" callout that may be appended to the market-events section. */
export const newsletterEssaySchema = z.object({
  label: z.string(),
  headline: z.string(),
  body: z.string(),
})

/**
 * One row of "Sectors this week".
 *
 * `change` is a percent (`percentSchema`, the `Money`-branded alias) while
 * `open`/`close` are index levels. All three are nullable for the same reason
 * and must render as a dash, never `0.00`.
 */
export const newsletterSectorRowSchema = z.object({
  sector: z.string(),
  open: moneySchema.nullable(),
  close: moneySchema.nullable(),
  change: percentSchema.nullable(),
})

// ── The four section kinds ───────────────────────────────────────────────────

export const indexSummarySectionSchema = z.object({
  type: z.literal('index_summary'),
  title: z.string(),
  metrics: z.array(newsletterMetricSchema),
  series: z.array(newsletterSeriesPointSchema),
})

export const marketEventsSectionSchema = z.object({
  type: z.literal('market_events'),
  title: z.string(),
  events: z.array(newsletterMarketEventSchema),
  /** Absent on most issues. `null`, never an empty object. */
  essay: newsletterEssaySchema.nullable(),
})

export const didYouKnowSectionSchema = z.object({
  type: z.literal('did_you_know'),
  title: z.string(),
  body: z.string(),
})

export const sectorTableSectionSchema = z.object({
  type: z.literal('sector_table'),
  title: z.string(),
  rows: z.array(newsletterSectorRowSchema),
})

/**
 * The reader body: a real discriminated union, not a bag with optional keys.
 *
 * `z.discriminatedUnion` rather than `z.union` for two reasons. It narrows on
 * `section.type` so a renderer written as a `switch` is exhaustive at compile
 * time and gains a compile error — not a blank block — the day a fifth kind
 * lands. And it reports a bad payload against ONE member instead of unioning
 * four sets of errors, so a drifted section names itself.
 *
 * It is STRICT: an unrecognised `type` fails the parse. That is the house rule
 * ("contract drift fails at the call, not three components later") and it is
 * safe here because the contract is closed by construction — mm-be's
 * `parse_sections` only ever emits these four and SKIPS anything else, so a
 * fifth kind cannot appear without a deliberate backend change. The cost is
 * real and worth stating: when that change comes, the whole issue fails to
 * parse rather than degrading to three of four blocks. Adding the member here
 * is the same release.
 */
export const newsletterSectionSchema = z.discriminatedUnion('type', [
  indexSummarySectionSchema,
  marketEventsSectionSchema,
  didYouKnowSectionSchema,
  sectorTableSectionSchema,
])

// ── The two surfaces ─────────────────────────────────────────────────────────

/** The archive card: identity and preview, no body. */
export const newsletterCardSchema = z.object({
  /** "Issue 20" — and the detail route's key. NOT `slug`, which is decorative. */
  issueNumber: z.number().int().positive(),
  slug: z.string(),
  /** The masthead, e.g. "MoneyMitra Weekly". */
  source: z.string(),
  headline: z.string(),
  /** May be blank — `allow_blank=True` on the serialiser. No `.min(1)`. */
  summary: z.string(),
  /** Blank is normalised to `null` by mm-be, so empty string never arrives. */
  coverImage: z.string().nullable(),
  /** `null` when the read time has not been computed yet — not "0 minutes". */
  readTimeMinutes: z.number().int().nonnegative().nullable(),
  publishedAt: isoDateTimeSchema.nullable(),
})

/** The reader page: the card, plus the ordered section blocks. */
export const newsletterIssueSchema = newsletterCardSchema.extend({
  /** Ordered by the issue's layout. EMPTY for an issue with no blocks yet. */
  sections: z.array(newsletterSectionSchema),
})

/** One page of the archive. */
export const newsletterArchivePageSchema = z.object({
  rows: z.array(newsletterCardSchema),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  /** Every published issue, not the rows on this page — easiest field in the
   *  list envelope's `meta` to get wrong; see the note in
   *  `newsletterService.ts`. */
  total: z.number().int().nonnegative(),
})

// ── Inferred types ───────────────────────────────────────────────────────────

export type NewsletterMetric = z.infer<typeof newsletterMetricSchema>
export type NewsletterSeriesPoint = z.infer<typeof newsletterSeriesPointSchema>
export type NewsletterMarketEvent = z.infer<typeof newsletterMarketEventSchema>
export type NewsletterEssay = z.infer<typeof newsletterEssaySchema>
export type NewsletterSectorRow = z.infer<typeof newsletterSectorRowSchema>

export type IndexSummarySection = z.infer<typeof indexSummarySectionSchema>
export type MarketEventsSection = z.infer<typeof marketEventsSectionSchema>
export type DidYouKnowSection = z.infer<typeof didYouKnowSectionSchema>
export type SectorTableSection = z.infer<typeof sectorTableSectionSchema>
export type NewsletterSection = z.infer<typeof newsletterSectionSchema>

export type NewsletterCard = z.infer<typeof newsletterCardSchema>
export type NewsletterIssue = z.infer<typeof newsletterIssueSchema>
export type NewsletterArchivePage = z.infer<typeof newsletterArchivePageSchema>
